import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';
import NotificationItem from './NotificationItem';

export const NotificationDropdown: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { pubkey } = useAuth();
    const { events, isLoading, unreadCount, markAsRead } = useNotifications(pubkey);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Mark as read when opening dropdown
    useEffect(() => {
        if (isOpen && unreadCount > 0 && markAsRead) {
            markAsRead();
        }
    }, [isOpen, unreadCount, markAsRead]);

    // Limit to 5 notifications for the dropdown
    const recentEvents = events.slice(0, 5);
    // const hasUnread = recentEvents.length > 0; // Removed simplified logic

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
          p-2 rounded-xl transition-all relative
          ${isOpen
                        ? 'bg-mavi-50 text-mavi-600 dark:bg-mavi-900/20 dark:text-mavi-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-surface-100 dark:hover:bg-surface-800'
                    }
        `}
                title="Bildirimler"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && !isOpen && (
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-surface-900" />
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-80 md:w-96 bg-white dark:bg-surface-900 rounded-2xl shadow-xl border border-surface-200 dark:border-surface-700 overflow-hidden z-50 origin-top-right"
                    >
                        <div className="p-4 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between">
                            <h3 className="font-poppins font-semibold text-sm text-gray-900 dark:text-white">
                                Bildirimler
                            </h3>
                            <Link
                                to="/notifications"
                                onClick={() => setIsOpen(false)}
                                className="text-xs text-mavi-600 dark:text-mavi-400 hover:underline font-medium"
                            >
                                Tümünü Gör
                            </Link>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto">
                            {isLoading ? (
                                <div className="p-4 space-y-3">
                                    {[...Array(3)].map((_, i) => (
                                        <div key={i} className="flex gap-3 animate-pulse">
                                            <div className="w-8 h-8 bg-surface-200 dark:bg-surface-800 rounded-full" />
                                            <div className="flex-1 space-y-2">
                                                <div className="h-3 bg-surface-200 dark:bg-surface-800 rounded w-3/4" />
                                                <div className="h-2 bg-surface-200 dark:bg-surface-800 rounded w-1/2" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : recentEvents.length > 0 ? (
                                <ul className="divide-y divide-gray-100 dark:divide-surface-800">
                                    {recentEvents.map(event => (
                                        <div key={event.id} onClick={() => setIsOpen(false)}>
                                            <NotificationItem event={event} />
                                        </div>
                                    ))}
                                </ul>
                            ) : (
                                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-xs font-poppins">Henüz bildiriminiz yok.</p>
                                </div>
                            )}
                        </div>


                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
